import { Linkedin, Unplug, Check, AlertTriangle } from "lucide-react";
import { inputCls } from "@agent/lib/styles";

export function LinkedinSettingsCard({ status, orgId, setOrgId, onConnect, onDisconnect, onSaveOrg }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-display font-bold text-[#0f172a] text-sm flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-[#0a66c2]/10 flex items-center justify-center"><Linkedin size={15} className="text-[#0a66c2]" /></span>
          LinkedIn Business Page
        </h2>
        {status && (
          <span data-testid="linkedin-status-badge" className={`text-[10px] tracking-[0.15em] font-semibold px-3 py-1 rounded-full border uppercase ${
            status.connected ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-rose-50 text-rose-500 border-rose-200"}`}>
            {status.connected ? "Connected" : status.needs_reconnect ? "Reconnect needed" : "Not connected"}
          </span>
        )}
      </div>
      <div className="p-6 space-y-5">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs space-y-1.5">
          <p className="text-slate-500">Client ID: <span className="text-[#0f172a] font-semibold mono">{status?.client_id_masked || "…"}</span></p>
          <p className="text-slate-500">Client secret: <span className="text-[#0f172a] font-semibold mono">••••••••••••••••</span> <span className="text-slate-400">(stored server-side only)</span></p>
          {status?.scope && <p className="text-slate-500">Scope: <span className="text-[#0f172a] font-semibold mono">{status.scope}</span></p>}
          {status?.expires_at && <p className="text-slate-500">Token expires: <span className="text-[#0f172a] font-semibold">{new Date(status.expires_at * 1000).toLocaleString()}</span></p>}
        </div>

        <div className="border border-amber-200 bg-amber-50/60 rounded-xl p-4 flex gap-3">
          <AlertTriangle size={15} className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600 leading-relaxed">
            Add this redirect URL in your LinkedIn app (Auth → OAuth 2.0 settings):<br />
            <code className="text-amber-700 break-all font-medium">{status?.redirect_uri}</code><br />
            Your <code className="text-[#0f172a] font-medium">Community Management API</code> access for <code className="text-[#0f172a] font-medium">w_organization_social</code> is approved — click Connect below to finish linking your account.
          </p>
        </div>

        <div className="flex gap-2">
          {!status?.connected ? (
            <button data-testid="linkedin-connect-btn" onClick={onConnect}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#0a66c2] text-white text-sm font-semibold hover:bg-[#0a5cb0] transition-colors duration-150">
              <Linkedin size={14} /> Connect LinkedIn
            </button>
          ) : (
            <button data-testid="linkedin-disconnect-btn" onClick={onDisconnect}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full border border-rose-200 bg-rose-50 text-rose-600 text-sm font-semibold hover:bg-rose-100 transition-colors duration-150">
              <Unplug size={14} /> Disconnect
            </button>
          )}
        </div>

        <div className="border-t border-slate-100 pt-5">
          {status?.connected && !status?.org_id && (
            <div className="border border-rose-200 bg-rose-50/60 rounded-xl p-3 mb-3 flex gap-2" data-testid="org-id-missing-warning">
              <AlertTriangle size={14} className="text-rose-500 shrink-0 mt-0.5" />
              <p className="text-xs text-rose-700 leading-relaxed">
                Connected, but Publish won't work until you set and save your Organization ID below.
              </p>
            </div>
          )}
          <label className="text-[11px] tracking-[0.2em] uppercase text-slate-400 font-semibold block mb-1.5">Organization / Page ID</label>
          <p className="text-xs text-slate-400 mb-2">Numeric ID from your Page admin URL: linkedin.com/company/<b className="text-slate-600">&lt;id&gt;</b>/admin — or paste the admin URL.</p>
          <div className="flex gap-2">
            <input data-testid="org-id-input" className={inputCls} value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="e.g. 12345678" />
            <button data-testid="org-id-save-btn" onClick={onSaveOrg}
              className="flex items-center gap-2 px-6 rounded-full bg-[#0f172a] text-white text-sm font-semibold hover:bg-[#1e293b] transition-colors duration-150 whitespace-nowrap">
              <Check size={14} /> Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
